import {
  Button,
  type ButtonProps,
  Column,
  ConfirmationDialog,
  CloseIcon,
  DeleteIcon,
  HiddenFormLabel,
  IconButton,
  type IconButtonProps,
  MenuItem,
  Text,
  TextInput,
  useToast,
} from "@hightouch/htui";
import { captureException, isUserFacingError } from "src/lib/monitoring";
import { capitalize } from "lodash";
import { forwardRef, type ReactNode, useState } from "react";

/** The words and icon for each kind of destructive action. */
const ACTIONS = {
  delete: {
    verb: "delete",
    Verb: "Delete",
    past: "deleted",
    noun: "deletion",
    icon: DeleteIcon,
  },
  remove: {
    verb: "remove",
    Verb: "Remove",
    past: "removed",
    noun: "removal",
    icon: CloseIcon,
  },
} as const;

type Props = {
  label: string;
  /** "remove" for taking something out of a group without deleting it, such as a member from a team. */
  action?: keyof typeof ACTIONS;
  onDelete: () => Promise<unknown>;
  onSuccess?: () => void;
  isDisabled?: ButtonProps["isDisabled"];
  size?: ButtonProps["size"];
  variant?: "button" | "icon" | "menu";
  iconVariant?: IconButtonProps["variant"];
  additionalConfirmation?: ReactNode;
  typeValueToConfirm?: string;
};

/**
 * Note: when using this component as a menu item, you must set `<Menu isLazy={false}>` on the parent menu,
 * otherwise the confirmation dialog will unmount when the menu is closed.
 */
export const DeleteButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      label,
      onDelete,
      size = "lg",
      variant = "button",
      iconVariant = "danger",
      onSuccess,
      additionalConfirmation,
      typeValueToConfirm,
      action = "delete",
      ...props
    },
    ref,
  ) => {
    const { verb, Verb, past, noun, icon } = ACTIONS[action];
    const { toast } = useToast();
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmValue, setConfirmValue] = useState("");

    const onClick = () => {
      setIsDeleting(true);
    };

    const isStringConfirmation = typeof additionalConfirmation === "string";
    const confirmationMessage = `Are you sure you want to ${verb} this ${label}?${isStringConfirmation ? ` ${additionalConfirmation}` : ""}`;

    return (
      <>
        {variant === "icon" ? (
          <IconButton
            aria-label={`${Verb} ${label}`}
            variant={iconVariant}
            icon={icon}
            onClick={onClick}
            {...props}
            ref={ref}
          />
        ) : variant === "menu" ? (
          <MenuItem
            icon={icon}
            variant="danger"
            onClick={onClick}
            {...props}
            ref={ref}
          >
            {Verb}
          </MenuItem>
        ) : (
          <Button
            size={size}
            variant="warning"
            onClick={onClick}
            {...props}
            ref={ref}
          >
            {Verb}
          </Button>
        )}
        <ConfirmationDialog
          isOpen={isDeleting}
          onClose={() => {
            setIsDeleting(false);
            setConfirmValue("");
          }}
          onConfirm={async () => {
            try {
              await onDelete();
              toast({
                id: "delete-resource",
                title: `${capitalize(label)} ${past}`,
                variant: "success",
              });
              onSuccess?.();
            } catch (e) {
              toast({
                id: "delete-resource",
                title: `Failed to ${verb} ${label}`,
                message: isUserFacingError(e) ? e.message : undefined,
                variant: "error",
              });
              captureException(e);
            }
          }}
          title={`${Verb} ${label}`}
          confirmButtonText={Verb}
          variant="danger"
          isConfirmButtonDisabled={
            Boolean(typeValueToConfirm) && typeValueToConfirm !== confirmValue
          }
        >
          <Column gap={2}>
            <Text>{confirmationMessage}</Text>
            {!isStringConfirmation && additionalConfirmation}
          </Column>
          {typeValueToConfirm && (
            <Column>
              <Text mt={4} mb={2}>
                Type <Text fontWeight="semibold">{typeValueToConfirm}</Text> to
                confirm:
              </Text>
              <HiddenFormLabel
                label={`Type ${typeValueToConfirm} to confirm ${noun}`}
              >
                <TextInput
                  mb={1}
                  placeholder={typeValueToConfirm}
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value)}
                />
              </HiddenFormLabel>
            </Column>
          )}
        </ConfirmationDialog>
      </>
    );
  },
);

DeleteButton.displayName = "DeleteButton";
